/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { DataType, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { getMapZoneCoordinates, setMapZoneCoordinates } from 'apis/setup/mobile/MapZone';
import { deleteCoordinates } from 'apis/setup/mobile/BoxLocation';
import { MapZoneCoordinate } from 'models/setup/mobile/MapZone';
import { CommonYN } from 'models/common/Common';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import CustomGrid from 'components/grids/CustomGrid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';

interface SearchParamData {
  sMapZonId: string;
  editable: boolean;
  sZonCd: string;
  sZonNm: string;
}

const ZoneCoordinateGrid = (props: SearchParamData) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [rowData, setRowData] = useState<MapZoneCoordinate[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'crudKey',
      header: String(t('com.label.상태', '상태')),
      width: 47,
      isReadOnly: true,
      cellTemplate: GridStatusCellTemplate,
    },
    {
      header: String(t('setup.grid.구역코드', '구역코드')),
      binding: 'mapZonId',
      width: 120,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.grid.구역명', '구역명')),
      binding: 'zonNm',
      width: 150,
      align: 'left',
      isReadOnly: true,
    },
    {
      binding: 'xcrd',
      header: String(t('setup.grid.위도', '위도')),
      align: 'right',
      width: 200,
      dataType: DataType.Number,
      format: 'n8',
    },
    {
      binding: 'ycrd',
      header: String(t('setup.grid.경도', '경도')),
      align: 'right',
      width: 200,
      dataType: DataType.Number,
      format: 'n8',
    },
    {
      binding: 'dataInsUserId',
      header: String(t('com.label.등록자', '등록자')),
      width: 120,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'dataInsDtm',
      header: String(t('com.label.등록일시', '등록일시')),
      width: 160,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'dataUpdUserId',
      header: String(t('com.label.수정자', '수정자')),
      width: 120,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일시', '수정일시')),
      width: 160,
      align: 'center',
      isReadOnly: true,
    },
  ];

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'coordinates';
    book.saveAsync(getExcelFileName(t('setup.label.좌표목록', '좌표목록')));
  };

  const fnSearchCoordinates = useCallback((params: string) => {
    getMapZoneCoordinates(params).then((result: MapZoneCoordinate[]) => {
      setRowData(result);
      gridRef.current?.refresh();
    });
  }, []);

  const btnAddRow = useCallback(() => {
    if (!props.sMapZonId) {
      openMessageBar({
        type: 'error',
        content: t(
          'setup.msg.구역 목록에서 상세 버튼을 클릭하여 구역을 먼저 선택해 주세요.',
          '구역 목록에서 상세 버튼을 클릭하여 구역을 먼저 선택해 주세요.'
        ),
      });
      return;
    }

    const newRow = {
      crudKey: CrudCode.CREATE,
      mapZonId: props.sMapZonId,
      zonCd: props.sZonCd,
      zonNm: props.sZonNm,
    } as MapZoneCoordinate;

    setRowData([newRow, ...rowData]);
  }, [rowData, props.sMapZonId, t, openMessageBar]);

  const btnDelRow = useCallback(() => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (selectedRows.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('setup.msg.삭제할 좌표를 선택해 주세요.', '삭제할 좌표를 선택해 주세요.'),
      });
      return;
    }

    const newRowData = [...rowData];
    selectedRows.forEach((row) => {
      const item = row.dataItem;
      if (item.crudKey === CrudCode.CREATE) {
        const idx = newRowData.indexOf(item);
        if (idx > -1) newRowData.splice(idx, 1);
      } else {
        item.crudKey = CrudCode.DELETE;
      }
    });
    setRowData(newRowData);
  }, [rowData, t, openMessageBar]);

  const btnCancelDelRow = useCallback(() => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    const deleteSelectedRows = selectedRows.filter((r) => r.dataItem.crudKey === CrudCode.DELETE);

    if (deleteSelectedRows.length < 1) {
      openMessageBar({
        type: 'error',
        content: t(
          'setup.msg.삭제취소할 좌표를 선택해 주세요.',
          '삭제취소할 좌표를 선택해 주세요.'
        ),
      });
      return;
    }

    const newRowData = [...rowData];
    deleteSelectedRows.forEach((row) => {
      const item = row.dataItem;
      item.crudKey = null;
    });
    setRowData(newRowData);
  }, [rowData, t, openMessageBar]);

  const hasDeleteRows = rowData.some((r) => r.crudKey === CrudCode.DELETE);

  const btnSave = async () => {
    // DELETE 상태가 아닌 행만 validation
    const valid = rowData
      .filter((r) => r.crudKey !== CrudCode.DELETE)
      .map((rowNode, index) => {
        if (rowNode.xcrd == null || rowNode.xcrd === undefined)
          return `${index + 1} : ${t(
            'setup.msg.X좌표를 입력해 주세요.',
            'X좌표를 입력해 주세요.'
          )}`;
        if (rowNode.ycrd == null || rowNode.ycrd === undefined)
          return `${index + 1} : ${t(
            'setup.msg.Y좌표를 입력해 주세요.',
            'Y좌표를 입력해 주세요.'
          )}`;
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    // CREATE/UPDATE 행
    const saveRows = rowData.filter(
      (r) => r.crudKey === CrudCode.CREATE || r.crudKey === CrudCode.UPDATE
    );

    // DELETE 행
    const deleteRows = rowData.filter((r) => r.crudKey === CrudCode.DELETE);

    if (saveRows.length === 0 && deleteRows.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        try {
          const promises: Promise<any>[] = [];
          if (saveRows.length > 0) promises.push(setMapZoneCoordinates(saveRows));
          if (deleteRows.length > 0)
            promises.push(
              deleteCoordinates(
                deleteRows.map((r) => r.mapZonCrdId).filter((id): id is string => !!id)
              )
            );

          await Promise.all(promises);
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          fnSearchCoordinates(props.sMapZonId);
        } catch (error) {
          openMessageBar({
            type: 'error',
            content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
        }
      },
      noCallback: () => {
        return false;
      },
    });
  };

  useEffect(() => {
    if (props.sMapZonId) {
      fnSearchCoordinates(props.sMapZonId);
    } else {
      setRowData([]);
    }
  }, [props.sMapZonId]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      const isReadOnly = item.crudKey !== CrudCode.CREATE;
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    setflexRef(grid);
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.좌표목록', '좌표목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {props.editable && (
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={btnExcelExport}
              disableRipple
            >
              {t('com.button.다운로드', '다운로드')}
            </Button>
            <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
              {t('com.button.행추가', '행추가')}
            </Button>
            <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
              {t('com.button.행삭제', '행삭제')}
            </Button>
            {hasDeleteRows && (
              <Button
                css={IconButton.button}
                className="delRow"
                disableRipple
                onClick={btnCancelDelRow}
              >
                {t('com.button.행삭제취소', '행삭제취소')}
              </Button>
            )}
          </ControlBtnGroup>
        )}
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={200}
        deferResizing={false}
        initialized={onInitialized}
        align={'center'}
        beginningEdit={(grid, e) => {
          const binding = grid.columns[e.col].binding;
          const item = grid.rows[e.row].dataItem;
          // readonly 설정은 layoutDefinition의 isReadOnly로 제어
        }}
      />
      <GlobalBtnGroup>
        {props.editable && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default memo(ZoneCoordinateGrid);
